import { DateTime } from 'luxon';
import {
  Availability,
  MenuItemV2Response,
} from 'components/src/types/menu.types';

/**
 * How long a sold out item stays off the menu. `custom` sends `pauseUntil`,
 * `endOfDay` the older `pauseUntilEndOfTheDay`, `indefinitely` neither.
 */
export type PauseChoice = 'indefinitely' | 'endOfDay' | 'custom';

export const PAUSE_CHOICE_LABELS: Record<PauseChoice, string> = {
  indefinitely: 'Out of Stock (Indefinitely)',
  endOfDay: 'Out of Stock (Till End of Day)',
  custom: 'Out of Stock (Custom Date)',
};

export type ItemPause = {
  availability: Availability;
  pauseUntilEndOfTheDay: boolean;
  /** Left out of the body unless it is set - only `soldOut` may carry it. */
  pauseUntil?: string;
};

export const pauseUntilFormat = "EEE, MMM d '·' h:mm a";

/*
 * The backend reads `pauseUntil` in the LOCATION's time zone, while the date
 * picker hands back a Date in the browser's. So a picked Date is only ever a
 * wall clock: its fields are what the merchant chose, the zone is ignored.
 * `timeZone` is undefined until the location has loaded, and then the browser's
 * zone stands in.
 */

export const zone = (timeZone?: string) => timeZone || 'local';

export const wallClockToZone = (wallClock: Date, timeZone?: string) => {
  const { year, month, day, hour, minute } = DateTime.fromJSDate(wallClock);
  return DateTime.fromObject(
    { year, month, day, hour, minute },
    { zone: zone(timeZone) }
  );
};

export const zoneToWallClock = (moment: DateTime) =>
  DateTime.local(
    moment.year,
    moment.month,
    moment.day,
    moment.hour,
    moment.minute
  ).toJSDate();

/** `2026-10-20T14:00`: a moment in the location's zone, without an offset. */
export const toPauseUntil = (wallClock: Date) =>
  DateTime.fromJSDate(wallClock).toFormat("yyyy-MM-dd'T'HH:mm");

/** A past `pauseUntil` answers 400, so it is caught before sending. */
export const isPauseInFuture = (wallClock: Date, timeZone?: string) =>
  wallClockToZone(wallClock, timeZone) > DateTime.now();

export const PAUSE_IN_PAST_ERROR = 'Please pick a time in the future';

/** What the picker opens on: the top of the hour after the next one. */
export const defaultPauseUntil = (timeZone?: string) =>
  zoneToWallClock(
    DateTime.now().setZone(zone(timeZone)).plus({ hours: 1 }).startOf('hour')
  );

/** `unpauseAt` as the location's wall clock, for the picker. */
export const unpauseAtToWallClock = (unpauseAt: string, timeZone?: string) =>
  zoneToWallClock(DateTime.fromISO(unpauseAt).setZone(zone(timeZone)));

/**
 * Which option a stored pause reads as. Any pause with an end reads as a
 * custom one at its `unpauseAt` - "Till End of Day" included, which the
 * backend ends at 4 AM, not midnight - so saving the item again keeps it.
 * `pausedUntilEndOfTheDay` now only means "the pause has an end", so it is
 * trusted only when an older API build sends no `unpauseAt` at all.
 */
export const pauseChoiceOf = (item: MenuItemV2Response): PauseChoice => {
  if (item.unpauseAt === undefined) {
    return item.pausedUntilEndOfTheDay ? 'endOfDay' : 'indefinitely';
  }
  return item.unpauseAt ? 'custom' : 'indefinitely';
};

/** The pill on a sold out item, in the location's time zone. */
export const pausedItemLabel = (item: MenuItemV2Response, timeZone?: string) =>
  item.unpauseAt
    ? `Paused until ${DateTime.fromISO(item.unpauseAt)
        .setZone(zone(timeZone))
        .toFormat('MMM d, h:mm a')}`
    : PAUSE_CHOICE_LABELS[pauseChoiceOf(item)];

/**
 * Said under the picker when the location keeps a different clock than the
 * browser, since the time picked is the location's. Null when they agree.
 */
export const locationZoneNote = (timeZone?: string) => {
  if (!timeZone) return null;
  const there = DateTime.now().setZone(timeZone);
  if (!there.isValid || there.offset === DateTime.now().offset) return null;
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ');
  return `Times are in ${city} time, where it is now ${there.toFormat(
    'h:mm a'
  )}.`;
};

export const itemPauseBody = (
  choice: PauseChoice,
  until: Date | null
): ItemPause => ({
  availability: Availability.SoldOut,
  pauseUntilEndOfTheDay: choice === 'endOfDay',
  ...(choice === 'custom' && until ? { pauseUntil: toPauseUntil(until) } : {}),
});
